/**
 * OASIS Service - MVP Implementation
 * Handles OASIS assessment CRUD operations and HIPPS code calculation
 */

import { apiClient } from '../api-client';
import { ApiResponse } from '../../types/api';
import {
  OasisAssessment,
  OasisListItem,
  OasisFormData,
  OasisType,
  OasisResponses,
  HippsCodeCalculation,
  OasisValidationResult,
  OasisValidationError
} from '../../types/patientmr/oasis';
import { AssessmentStatus } from './assessments.service';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { mvpOasisFields, getRequiredFields, validateFieldDependencies } from '../../config/oasis-fields';

export class OasisService {
  private static readonly baseUrl = '/oasis';

  /**
   * Get all OASIS assessments for a patient
   */
  static async getOasisAssessments(patientId: string): Promise<OasisListItem[]> {
    try {
      const response: ApiResponse<OasisListItem[]> = await apiClient.get(
        `${this.baseUrl}/patient/${patientId}`
      );

      if (response.success && response.data) {
        return this.formatOasisList(response.data);
      }

      throw new Error(response.error?.message || 'Failed to fetch OASIS assessments');
    } catch (error) {
      console.error('Error fetching OASIS assessments:', error);
      throw error;
    }
  }

  /**
   * Get a specific OASIS assessment by ID
   */
  static async getOasisAssessment(patientId: string, assessmentId: string): Promise<OasisAssessment> {
    try {
      const response: ApiResponse<OasisAssessment> = await apiClient.get(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}`
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to fetch OASIS assessment');
    } catch (error) {
      console.error('Error fetching OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Create a new OASIS assessment
   */
  static async createOasisAssessment(patientId: string, formData: OasisFormData): Promise<OasisAssessment> {
    try {
      // Validate the form data before submission
      const validationResult = this.validateOasisData(formData);
      if (!validationResult.isValid) {
        throw new Error(`Validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`);
      }

      const response: ApiResponse<OasisAssessment> = await apiClient.post(
        `${this.baseUrl}/patient/${patientId}`,
        formData
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to create OASIS assessment');
    } catch (error) {
      console.error('Error creating OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Update an existing OASIS assessment
   */
  static async updateOasisAssessment(
    patientId: string,
    assessmentId: string,
    formData: OasisFormData
  ): Promise<OasisAssessment> {
    try {
      // Validate the form data before submission
      const validationResult = this.validateOasisData(formData);
      if (!validationResult.isValid) {
        throw new Error(`Validation failed: ${validationResult.errors.map(e => e.message).join(', ')}`);
      }

      const response: ApiResponse<OasisAssessment> = await apiClient.put(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}`,
        formData
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to update OASIS assessment');
    } catch (error) {
      console.error('Error updating OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Submit an OASIS assessment (change status to submitted)
   */
  static async submitOasisAssessment(patientId: string, assessmentId: string): Promise<OasisAssessment> {
    try {
      const response: ApiResponse<OasisAssessment> = await apiClient.post(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}/submit`
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to submit OASIS assessment');
    } catch (error) {
      console.error('Error submitting OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Lock an OASIS assessment (prevent further edits)
   */
  static async lockOasisAssessment(patientId: string, assessmentId: string): Promise<OasisAssessment> {
    try {
      const response: ApiResponse<OasisAssessment> = await apiClient.post(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}/lock`
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to lock OASIS assessment');
    } catch (error) {
      console.error('Error locking OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Reopen an OASIS assessment (change status back to DRAFT)
   */
  static async reopenOasisAssessment(patientId: string, assessmentId: string): Promise<OasisAssessment> {
    try {
      const response: ApiResponse<OasisAssessment> = await apiClient.post(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}/reopen`
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to reopen OASIS assessment');
    } catch (error) {
      console.error('Error reopening OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Start QA review for an OASIS assessment (change status to QA)
   */
  static async startQAOasisAssessment(patientId: string, assessmentId: string): Promise<OasisAssessment> {
    try {
      const response: ApiResponse<OasisAssessment> = await apiClient.post(
        `${this.baseUrl}/patient/${patientId}/assessment/${assessmentId}/startQA`
      );

      if (response.success && response.data) {
        return this.formatOasisAssessment(response.data);
      }

      throw new Error(response.error?.message || 'Failed to start QA for OASIS assessment');
    } catch (error) {
      console.error('Error starting QA for OASIS assessment:', error);
      throw error;
    }
  }

  /**
   * Calculate HIPPS code for given OASIS responses
   */
  static async calculateHippsCode(responses: OasisResponses): Promise<HippsCodeCalculation> {
    try {
      const response: ApiResponse<HippsCodeCalculation> = await apiClient.post(
        `${this.baseUrl}/calculate-hipps`,
        { responses }
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.error?.message || 'Failed to calculate HIPPS code');
    } catch (error) {
      console.error('Error calculating HIPPS code:', error);
      throw error;
    }
  }

  /**
   * Validate OASIS assessment data
   */
  static validateOasisData(formData: OasisFormData): OasisValidationResult {
    const errors: OasisValidationError[] = [];
    const warnings: OasisValidationError[] = [];

    // Get required fields
    const requiredFields = getRequiredFields();

    // Check required fields
    requiredFields.forEach(field => {
      const value = formData.responses[field.key as keyof OasisResponses];

      // Skip validation if field depends on other fields and conditions aren't met
      if (!validateFieldDependencies(formData.responses, field)) {
        return;
      }

      if (value === undefined || value === null || value === '') {
        errors.push({
          field: field.key,
          message: `${field.label} is required`,
          severity: 'error'
        });
      }
    });

    // Validate specific business rules
    this.validateBusinessRules(formData, errors, warnings);

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Validate OASIS business rules
   */
  private static validateBusinessRules(
    formData: OasisFormData,
    errors: OasisValidationError[],
    warnings: OasisValidationError[]
  ): void {
    const responses = formData.responses;

    // Rule 1: Assessment date should not be in the future
    if (formData.assessmentDate && new Date(formData.assessmentDate) > new Date()) {
      errors.push({
        field: 'assessmentDate',
        message: 'Assessment date cannot be in the future',
        severity: 'error'
      });
    }

    // Rule 2: Start of care date should be before or equal to assessment date
    if (responses.M0030_startCareDate && formData.assessmentDate) {
      const socDate = new Date(responses.M0030_startCareDate);
      const assessmentDate = new Date(formData.assessmentDate);
      if (socDate > assessmentDate) {
        errors.push({
          field: 'M0030_startCareDate',
          message: 'Start of care date cannot be after assessment date',
          severity: 'error'
        });
      }
    }

    // Rule 3: Primary diagnosis should be specified
    if (!responses.M1021_primaryDiagnosis || responses.M1021_primaryDiagnosis.trim() === '') {
      errors.push({
        field: 'M1021_primaryDiagnosis',
        message: 'Primary diagnosis is required for HIPPS calculation',
        severity: 'error'
      });
    }

    // Rule 4: Therapy needs validation
    if (responses.M2200_therapyNeedSoc && responses.M2200_therapyNeedSoc !== '0') {
      // If therapy is needed, warn about ensuring proper documentation
      warnings.push({
        field: 'M2200_therapyNeedSoc',
        message: 'Ensure therapy orders and documentation are complete',
        severity: 'warning'
      });
    }

    // Rule 5: Cognitive function and ADL consistency
    if (responses.M1700_cognitiveFunction === '4' && responses.M1800_grooming === '0') {
      warnings.push({
        field: 'M1800_grooming',
        message: 'Grooming ability may be inconsistent with cognitive function level',
        severity: 'warning'
      });
    }

    // Discharge-specific validation rules
    const isDischarge = responses.M0100_thisAssessmentCompleted === '7' ||
                       responses.M0100_thisAssessmentCompleted === '8' ||
                       responses.M0100_thisAssessmentCompleted === '9';

    if (isDischarge) {
      // Rule 6: Discharge date is required for discharge assessments
      if (!responses.M3005_dischargeDate) {
        errors.push({
          field: 'M3005_dischargeDate',
          message: 'Discharge date is required for discharge assessments',
          severity: 'error'
        });
      } else {
        // Rule 7: Discharge date should not be before start of care date
        if (responses.M0030_startCareDate) {
          const dischargeDate = new Date(responses.M3005_dischargeDate);
          const socDate = new Date(responses.M0030_startCareDate);
          if (dischargeDate < socDate) {
            errors.push({
              field: 'M3005_dischargeDate',
              message: 'Discharge date cannot be before start of care date',
              severity: 'error'
            });
          }
        }

        // Rule 8: Discharge date should be on or before assessment date
        if (formData.assessmentDate) {
          const dischargeDate = new Date(responses.M3005_dischargeDate);
          const assessmentDate = new Date(formData.assessmentDate);
          if (dischargeDate > assessmentDate) {
            warnings.push({
              field: 'M3005_dischargeDate',
              message: 'Discharge date is after assessment date - please verify',
              severity: 'warning'
            });
          }
        }
      }

      // Rule 9: Discharge reason is required
      if (!responses.M3010_dischargeReason) {
        errors.push({
          field: 'M3010_dischargeReason',
          message: 'Discharge reason is required for discharge assessments',
          severity: 'error'
        });
      }

      // Rule 10: Emergency department use validation
      if (!responses.M3020_emergencyUse) {
        errors.push({
          field: 'M3020_emergencyUse',
          message: 'Emergency department use must be documented for discharge assessments',
          severity: 'error'
        });
      }

      // Rule 11: Discharge functional status validation - warn if all are independent (may indicate incomplete assessment)
      const dischargeFunctionalFields = [
        'M3100_dischargeGrooming',
        'M3110_dischargeDressing',
        'M3120_dischargeBathing',
        'M3130_dischargeTransferring',
        'M3140_dischargeAmbulation'
      ];

      const allIndependent = dischargeFunctionalFields.every(field => responses[field] === '0');
      if (allIndependent && responses.M3010_dischargeReason !== '1') {
        warnings.push({
          field: 'M3100_dischargeGrooming',
          message: 'All discharge functional items marked as independent - please verify assessment accuracy',
          severity: 'warning'
        });
      }

      // Rule 12: Death-specific validations
      if (responses.M0100_thisAssessmentCompleted === '7') {
        if (responses.M3010_dischargeReason !== '6') {
          warnings.push({
            field: 'M3010_dischargeReason',
            message: 'Assessment indicates death at home but discharge reason does not match',
            severity: 'warning'
          });
        }
      }

      // Rule 13: Transfer-specific validations
      if (responses.M0100_thisAssessmentCompleted === '8') {
        if (responses.M3010_dischargeReason !== '4') {
          warnings.push({
            field: 'M3010_dischargeReason',
            message: 'Assessment indicates transfer to inpatient facility but discharge reason does not match',
            severity: 'warning'
          });
        }
      }
    }
  }

  /**
   * Create empty OASIS form data for a specific type
   */
  static createEmptyOasisData(type: OasisType, patientId: string): OasisFormData {
    const today = new Date().toISOString().split('T')[0];

    return {
      patientId,
      assessmentDate: today,
      type,
      responses: {
        // Pre-populate with common defaults
        M0030_startCareDate: type === OasisType.OASIS_SOC ? today : undefined,
        M0100_thisAssessmentCompleted: type === OasisType.OASIS_SOC ? '1' :
                                       type === OasisType.OASIS_ROC ? '4' : '9'
      }
    };
  }

  /**
   * Map OASIS assessment to form data for editing
   */
  static mapAssessmentToFormData(assessment: OasisAssessment): OasisFormData {
    return {
      patientId: assessment.patientId,
      assessmentDate: assessment.assessmentDate,
      type: assessment.type,
      episodeStartDate: assessment.episodeStartDate,
      episodeEndDate: assessment.episodeEndDate,
      responses: assessment.responses
    };
  }

  /**
   * Get OASIS type display name
   */
  static getOasisTypeDisplayName(type: OasisType): string {
    switch (type) {
      case OasisType.OASIS_SOC:
        return 'Start of Care';
      case OasisType.OASIS_ROC:
        return 'Recertification';
      case OasisType.OASIS_DISCHARGE:
        return 'Discharge';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get OASIS status display name
   */
  static getOasisStatusDisplayName(status: AssessmentStatus): string {
    switch (status) {
      case AssessmentStatus.DRAFT:
        return 'Draft';
      case AssessmentStatus.SUBMITTED:
        return 'Submitted';
      case AssessmentStatus.QA:
        return 'QA';
      case AssessmentStatus.COMPLETE:
        return 'Complete';
      case AssessmentStatus.CURRENT:
        return 'Current';
      case AssessmentStatus.EXPIRED:
        return 'Expired';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if OASIS assessment can be edited
   */
  static canEditAssessment(assessment: OasisAssessment): boolean {
    return assessment.status === AssessmentStatus.DRAFT;
  }

  /**
   * Check if OASIS assessment can be submitted
   */
  static canSubmitAssessment(assessment: OasisAssessment): boolean {
    return assessment.status === AssessmentStatus.DRAFT;
  }

  /**
   * Check if OASIS assessment can be reopened
   */
  static canReopenAssessment(assessment: OasisAssessment): boolean {
    return assessment.status === AssessmentStatus.SUBMITTED ||
           assessment.status === AssessmentStatus.QA ||
           assessment.status === AssessmentStatus.COMPLETE;
  }

  /**
   * Check if OASIS assessment can be locked
   */
  static canLockAssessment(assessment: OasisAssessment): boolean {
    return assessment.status === AssessmentStatus.SUBMITTED;
  }

  /**
   * Check if QA can be started for an OASIS assessment
   */
  static canStartQAAssessment(assessment: OasisAssessment): boolean {
    return assessment.status === AssessmentStatus.SUBMITTED;
  }

  /**
   * Simple HIPPS code calculation (MVP version)
   * This is a simplified version - in production, you'd use the official CMS algorithm
   */
  static calculateSimpleHippsCode(responses: OasisResponses): HippsCodeCalculation {
    let clinicalPoints = 0;
    let functionalPoints = 0;
    let servicePoints = 0;

    // Clinical dimension - based on primary diagnosis and clinical factors
    if (responses.M1021_primaryDiagnosis) {
      clinicalPoints += 10; // Base points for having a diagnosis
    }

    // Functional dimension - based on ADL scores
    const adlFields = [
      'M1800_grooming',
      'M1810_currentDressing',
      'M1820_currentBathing',
      'M1830_currentToileting',
      'M1840_currentTransferring',
      'M1845_currentAmbulation'
    ];

    adlFields.forEach(field => {
      const value = responses[field as keyof OasisResponses];
      if (value && typeof value === 'string') {
        functionalPoints += parseInt(value) || 0;
      }
    });

    // Service dimension - based on therapy needs
    if (responses.M2200_therapyNeedSoc && responses.M2200_therapyNeedSoc !== '0') {
      servicePoints += parseInt(responses.M2200_therapyNeedSoc) || 0;
    }

    // Generate a simple HIPPS code (format: 5 characters)
    const totalPoints = clinicalPoints + functionalPoints + servicePoints;
    const hippsCode = `H${totalPoints.toString().padStart(4, '0')}`;

    return {
      hippsCode,
      points: totalPoints,
      description: `HIPPS Code calculated based on clinical, functional, and service factors`,
      paymentAmount: totalPoints * 50, // Simplified payment calculation
      factors: {
        clinical: clinicalPoints,
        functional: functionalPoints,
        service: servicePoints
      }
    };
  }

  /**
   * Helper function to format OASIS assessment data from the API response
   * @param assessment OASIS assessment data from the API
   * @returns Formatted OasisAssessment object
   */
  static formatOasisAssessment(assessment: any): OasisAssessment {
    return {
      assessmentId: assessment.assessmentId || assessment.id,
      patientId: assessment.patientId,
      tenantId: assessment.tenantId || '',
      assessmentDate: assessment.assessmentDate,
      type: assessment.type,
      status: assessment.status || AssessmentStatus.DRAFT,
      episodeStartDate: assessment.episodeStartDate,
      episodeEndDate: assessment.episodeEndDate,
      hippsCode: assessment.hippsCode,
      responses: assessment.responses || {},
      clinicianId: assessment.clinicianId || '',
      clinicianName: assessment.clinicianName || assessment.providerName || 'Unknown Provider',
      createdAt: assessment.createdAt || new Date().toISOString(),
      updatedAt: assessment.updatedAt || new Date().toISOString(),
      submittedAt: assessment.submittedAt,
      lockedAt: assessment.lockedAt,
      approvedAt: assessment.approvedAt,
      version: assessment.version || 1
    };
  }

  /**
   * Helper function to format a list of OASIS assessments from the API
   * @param assessments Array of OASIS assessments from the API
   * @returns Array of formatted OasisListItem objects
   */
  static formatOasisList(assessments: any[]): OasisListItem[] {
    return assessments.map(assessment => {
      return {
        assessmentId: assessment.assessmentId || assessment.id,
        patientId: assessment.patientId,
        assessmentDate: assessment.assessmentDate || new Date().toISOString().split('T')[0],
        type: assessment.type,
        status: assessment.status || AssessmentStatus.DRAFT,
        clinicianName: assessment.clinicianName || assessment.providerName || 'Unknown Provider',
        createdAt: assessment.createdAt || new Date().toISOString(),
        updatedAt: assessment.updatedAt || new Date().toISOString()
      } as OasisListItem; // Using type assertion since OasisListItem interface might have additional fields
    });
  }

  /**
   * Finalize an OASIS assessment
   * @param patientId The patient ID
   * @param assessmentId The OASIS assessment ID to finalize
   * @returns A Promise that resolves when the OASIS assessment is finalized
   */
  static async finalizeOasisAssessment(patientId: string, assessmentId: string): Promise<void> {
    try {
      const response: ApiResponse<void> = await apiClient.put(
        `/patientmr/${patientId}/assessments/${assessmentId}/finalize`
      );

      if (response.success) {
        return;
      }

      throw new Error(response.error?.message || 'Failed to finalize OASIS assessment');
    } catch (error) {
      console.error(`Error finalizing OASIS assessment ${assessmentId} for patient ${patientId}:`, error);
      throw error;
    }
  }
}
